import React from 'react';
import {useTranslation} from 'react-i18next';
import type {
	InputMarketProps,
	MarketNamingTemplateKeys,
} from '../MarketNamingTemplateField';

export const matchesMarketNamingTemplate = (
	markets: InputMarketProps[],
	value: string,
	templatesToUse: MarketNamingTemplateKeys,
): boolean =>
	markets.some(market =>
		market[templatesToUse]?.some(template =>
			new RegExp(template.template).test(value),
		),
	);

export const useMarketNamingTemplateValidation = (
	markets: InputMarketProps[],
	namingHint: string,
	templatesToUse: MarketNamingTemplateKeys,
): {
	validateFieldValue: (value: string) => boolean | string;
	isMarketSelected: boolean;
	placeholder: string;
} => {
	const {t} = useTranslation('components/marketnamingtemplatevalidation');

	const isMarketSelected = markets.length > 0;
	const placeholder = isMarketSelected
		? namingHint
		: t('FieldDisabledPlaceholder');
	// A market with no templates for this field has no naming rule to enforce.
	const hasMarketWithoutTemplate = markets.some(
		market => (market[templatesToUse]?.length ?? 0) === 0,
	);

	const validateFieldValue = React.useCallback(
		(value: string) =>
			!isMarketSelected ||
			hasMarketWithoutTemplate ||
			matchesMarketNamingTemplate(markets, value, templatesToUse) ||
			namingHint,
		[isMarketSelected, hasMarketWithoutTemplate, markets, namingHint, templatesToUse],
	);

	return {
		validateFieldValue,
		isMarketSelected,
		placeholder,
	};
};
